<template>
  <div v-if="isVisible" class="debug-panel">
    <!-- Toggle Button -->
    <button
      @click="togglePanel"
      :class="panelToggleClasses"
      class="debug-toggle"
      :title="isExpanded ? 'Collapse debug panel' : 'Expand debug panel'"
    >
      <svg
        v-if="!isExpanded"
        fill="currentColor"
        viewBox="0 0 20 20"
        class="w-5 h-5"
      >
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clip-rule="evenodd"
        ></path>
      </svg>
      <svg v-else fill="currentColor" viewBox="0 0 20 20" class="w-5 h-5">
        <path
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </button>

    <!-- Debug Panel Content -->
    <div v-if="isExpanded" class="debug-content">
      <!-- Header -->
      <div class="debug-header">
        <h3 class="text-lg font-semibold text-gray-900">🐞 Debug Panel</h3>
        <div class="flex space-x-2">
          <button
            @click="refreshData"
            class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
          >
            Refresh
          </button>
          <button
            @click="exportDebugInfo"
            class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
          >
            Export
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="debug-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="{
            'bg-blue-500 text-white': activeTab === tab.id,
            'bg-gray-100 text-gray-700 hover:bg-gray-200': activeTab !== tab.id,
          }"
          class="px-3 py-2 text-sm font-medium rounded-t-lg transition-colors"
        >
          {{ tab.icon }} {{ tab.name }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="debug-tab-content">
        <!-- Environment Tab -->
        <div v-if="activeTab === 'environment'" class="tab-panel">
          <div class="space-y-4">
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Environment Information
              </h4>
              <div class="bg-gray-50 p-3 rounded text-sm">
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <strong>Environment:</strong>
                    {{ debugData.environment.current }}
                  </div>
                  <div>
                    <strong>Debug Mode:</strong>
                    {{ debugData.environment.debugMode ? "ON" : "OFF" }}
                  </div>
                  <div>
                    <strong>Version:</strong>
                    {{ debugData.environment.version }}
                  </div>
                  <div>
                    <strong>Build Time:</strong>
                    {{ formatDate(debugData.environment.buildTime) }}
                  </div>
                  <div>
                    <strong>Git Branch:</strong>
                    {{ debugData.environment.gitBranch }}
                  </div>
                  <div>
                    <strong>Git Commit:</strong>
                    {{ debugData.environment.gitCommit.substring(0, 8) }}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Configuration Status
              </h4>
              <div class="space-y-2">
                <div
                  v-for="(status, key) in debugData.environment.configStatus"
                  :key="key"
                  class="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                >
                  <span class="capitalize"
                    >{{ key.replace(/([A-Z])/g, " $1") }}:</span
                  >
                  <span :class="getStatusColor(status)">
                    {{
                      typeof status === "boolean"
                        ? status
                          ? "Configured"
                          : "Not Configured"
                        : status
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Features Tab -->
        <div v-if="activeTab === 'features'" class="tab-panel">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="font-semibold text-gray-800">Feature Flags</h4>
              <span class="text-sm text-gray-600">
                {{ debugData.features.enabledCount }}/{{
                  debugData.features.totalCount
                }}
                enabled
              </span>
            </div>

            <div
              v-for="(category, categoryName) in debugData.features.categories"
              :key="categoryName"
              class="space-y-2"
            >
              <h5 class="font-medium text-gray-700 capitalize border-b pb-1">
                {{ categoryName }}
              </h5>
              <div class="grid grid-cols-1 gap-1">
                <div
                  v-for="(enabled, flagName) in category"
                  :key="flagName"
                  class="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                >
                  <span class="capitalize">{{
                    flagName.replace(/([A-Z])/g, " $1")
                  }}</span>
                  <span :class="enabled ? 'text-green-600' : 'text-gray-500'">
                    {{ enabled ? "✅ ON" : "❌ OFF" }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Tab -->
        <div v-if="activeTab === 'performance'" class="tab-panel">
          <div class="space-y-4">
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Performance Metrics
              </h4>
              <div class="bg-gray-50 p-3 rounded text-sm">
                <div v-if="debugData.performance.memory" class="mb-3">
                  <strong>Memory Usage:</strong>
                  <div class="mt-1 bg-gray-200 rounded-full h-2">
                    <div
                      class="bg-blue-500 h-2 rounded-full"
                      :style="{
                        width: debugData.performance.memory.percentage + '%',
                      }"
                    ></div>
                  </div>
                  <div class="text-xs text-gray-600 mt-1">
                    {{ debugData.performance.memory.used }}MB /
                    {{ debugData.performance.memory.limit }}MB
                  </div>
                </div>

                <div v-if="debugData.performance.coreWebVitals">
                  <strong>Core Web Vitals:</strong>
                  <div class="mt-2 space-y-1">
                    <template
                      v-for="(vital, name) in debugData.performance
                        .coreWebVitals"
                      :key="name"
                    >
                      <div
                        v-if="vital"
                        class="flex justify-between items-center"
                      >
                        <span class="uppercase">{{ name }}:</span>
                        <span :class="getVitalColor(vital.rating)">
                          {{ vital.value }}{{ name === "cls" ? "" : "ms" }} ({{
                            vital.rating
                          }})
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Recent Performance Events
              </h4>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <div
                  v-for="event in debugData.performance.recentEvents"
                  :key="event.id"
                  class="text-xs p-2 bg-gray-50 rounded"
                >
                  <div class="font-medium">{{ event.name }}</div>
                  <div class="text-gray-600">
                    {{ event.duration }}ms - {{ formatTime(event.timestamp) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Validation Tab -->
        <div v-if="activeTab === 'validation'" class="tab-panel">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="font-semibold text-gray-800">System Validation</h4>
              <button
                @click="runValidation"
                class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
              >
                Run Check
              </button>
            </div>

            <div v-if="debugData.validation">
              <div class="bg-gray-50 p-3 rounded text-sm mb-3">
                <div class="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div class="text-green-600 font-semibold text-lg">
                      {{ debugData.validation.overall.passed }}
                    </div>
                    <div class="text-gray-600">Passed</div>
                  </div>
                  <div>
                    <div class="text-red-600 font-semibold text-lg">
                      {{ debugData.validation.overall.failed }}
                    </div>
                    <div class="text-gray-600">Failed</div>
                  </div>
                  <div>
                    <div class="text-orange-600 font-semibold text-lg">
                      {{ debugData.validation.overall.warnings }}
                    </div>
                    <div class="text-gray-600">Warnings</div>
                  </div>
                </div>
              </div>

              <div class="space-y-2 max-h-40 overflow-y-auto">
                <div
                  v-for="(category, categoryName) in debugData.validation
                    .categories"
                  :key="categoryName"
                >
                  <h5 class="font-medium text-gray-700 capitalize">
                    {{ categoryName }}
                  </h5>
                  <div class="ml-2 space-y-1">
                    <div
                      v-for="check in category.checks"
                      :key="check.name"
                      class="flex items-center justify-between text-xs p-1"
                    >
                      <span>{{ check.name }}</span>
                      <span :class="getValidationColor(check.result)">
                        {{
                          check.result === "pass"
                            ? "✅"
                            : check.result === "fail"
                            ? "❌"
                            : "⚠️"
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Monitoring Tab -->
        <div v-if="activeTab === 'monitoring'" class="tab-panel">
          <div class="space-y-4">
            <div>
              <h4 class="font-semibold text-gray-800 mb-2">
                Monitoring Configuration
              </h4>
              <div class="bg-gray-50 p-3 rounded text-sm">
                <div class="space-y-2">
                  <div
                    class="flex items-center justify-between p-2 bg-white rounded"
                  >
                    <span>Error Reporting:</span>
                    <span
                      :class="
                        debugData.monitoring?.errorReporting
                          ? 'text-green-600'
                          : 'text-gray-500'
                      "
                    >
                      {{
                        debugData.monitoring?.errorReporting
                          ? "✅ Enabled"
                          : "❌ Disabled"
                      }}
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between p-2 bg-white rounded"
                  >
                    <span>Sentry DSN:</span>
                    <span
                      :class="
                        debugData.monitoring?.hasSentryDsn
                          ? 'text-green-600'
                          : 'text-gray-500'
                      "
                    >
                      {{
                        debugData.monitoring?.hasSentryDsn
                          ? "✅ Configured"
                          : "❌ Not Configured"
                      }}
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between p-2 bg-white rounded"
                  >
                    <span>Analytics:</span>
                    <span
                      :class="
                        debugData.monitoring?.analytics
                          ? 'text-green-600'
                          : 'text-gray-500'
                      "
                    >
                      {{
                        debugData.monitoring?.analytics
                          ? "✅ Enabled"
                          : "❌ Disabled"
                      }}
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between p-2 bg-white rounded"
                  >
                    <span>Hotjar:</span>
                    <span
                      :class="
                        debugData.monitoring?.hotjar
                          ? 'text-green-600'
                          : 'text-gray-500'
                      "
                    >
                      {{
                        debugData.monitoring?.hotjar
                          ? "✅ Enabled"
                          : "❌ Disabled"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-800 mb-2">Sentry Testing</h4>
              <div
                class="bg-blue-50 border border-blue-200 p-3 rounded text-sm"
              >
                <p class="text-gray-700 mb-3">
                  Test that Sentry error reporting is working correctly by
                  triggering a test error.
                </p>
                <button
                  @click="testSentryError"
                  class="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-medium"
                >
                  🧪 Test Sentry Error
                </button>
                <p class="text-xs text-gray-600 mt-2">
                  Note: This will only send to Sentry if error reporting is
                  enabled and you're not in local mode.
                </p>
              </div>
            </div>

            <div v-if="sentryTestResult" class="bg-gray-50 p-3 rounded text-sm">
              <h5 class="font-semibold mb-2">Test Result:</h5>
              <div
                :class="
                  sentryTestResult.success ? 'text-green-700' : 'text-red-700'
                "
              >
                {{ sentryTestResult.message }}
              </div>
            </div>
          </div>
        </div>

        <!-- Logs Tab -->
        <div v-if="activeTab === 'logs'" class="tab-panel">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="font-semibold text-gray-800">Recent Logs</h4>
              <div class="flex space-x-2">
                <select
                  v-model="logFilter"
                  class="text-xs px-2 py-1 border rounded"
                >
                  <option value="all">All Levels</option>
                  <option value="error">Errors</option>
                  <option value="warn">Warnings</option>
                  <option value="info">Info</option>
                  <option value="debug">Debug</option>
                </select>
                <button
                  @click="clearLogs"
                  class="text-xs px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>

            <div class="space-y-1 max-h-80 overflow-y-auto text-xs font-mono">
              <div
                v-for="log in filteredLogs"
                :key="log.id"
                :class="getLogColor(log.level)"
                class="p-2 rounded border-l-4"
              >
                <div class="flex items-center justify-between">
                  <span class="font-semibold">{{
                    log.level.toUpperCase()
                  }}</span>
                  <span class="text-gray-500">{{
                    formatTime(log.timestamp)
                  }}</span>
                </div>
                <div>{{ log.message }}</div>
                <div v-if="log.data" class="mt-1 text-gray-600">
                  {{ JSON.stringify(log.data, null, 2) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import * as Sentry from "@sentry/vue";
import EnvironmentConfig from "../../config/environment.js";
import FeatureFlags from "../../config/features.js";
import ValidationService from "../../utils/validation.js";
import Logger from "../../utils/logger.js";

// Component state
const isExpanded = ref(false);
const activeTab = ref("environment");
const logFilter = ref("all");
const debugData = ref({});
const recentLogs = ref([]);
const sentryTestResult = ref(null);

// Props
const props = defineProps({
  position: {
    type: String,
    default: "bottom-right",
    validator: (value) =>
      ["bottom-right", "bottom-left", "top-right", "top-left"].includes(value),
  },
  maxLogs: {
    type: Number,
    default: 100,
  },
});

// Computed properties
const isVisible = computed(() => {
  // Multiple layers of safety - NEVER show in production
  const isProduction = EnvironmentConfig.isProduction;
  const isProductionNode = process.env.NODE_ENV === "production";
  const isProdEnvironment = process.env.VUE_APP_ENVIRONMENT === "production";

  // If ANY production indicator is true, hide the panel
  if (isProduction || isProductionNode || isProdEnvironment) {
    return false;
  }

  // Additional checks for non-production environments
  return (
    FeatureFlags.development.debugPanel &&
    EnvironmentConfig.shouldShowDebugInfo()
  );
});

const panelToggleClasses = computed(() => {
  const baseClasses = [
    "fixed",
    "z-40",
    "w-12",
    "h-12",
    "bg-gray-800",
    "text-white",
    "rounded-full",
    "shadow-lg",
    "hover:bg-gray-700",
    "transition-colors",
    "flex",
    "items-center",
    "justify-center",
  ];

  // Position based on prop
  const [vertical, horizontal] = props.position.split("-");

  if (vertical === "bottom") {
    baseClasses.push("bottom-4");
  } else {
    baseClasses.push("top-4");
  }

  if (horizontal === "right") {
    baseClasses.push("right-4");
  } else {
    baseClasses.push("left-4");
  }

  return baseClasses;
});

const filteredLogs = computed(() => {
  if (logFilter.value === "all") {
    return recentLogs.value;
  }
  return recentLogs.value.filter((log) => log.level === logFilter.value);
});

// Tab configuration
const tabs = ref([
  { id: "environment", name: "Environment", icon: "🌍" },
  { id: "features", name: "Features", icon: "🎛️" },
  { id: "performance", name: "Performance", icon: "📊" },
  { id: "validation", name: "Validation", icon: "🔍" },
  { id: "monitoring", name: "Monitoring", icon: "🔔" },
  { id: "logs", name: "Logs", icon: "📝" },
]);

// Methods
const togglePanel = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    refreshData();
  }
};

const refreshData = () => {
  collectDebugData();
};

const exportDebugInfo = () => {
  const debugInfo = {
    timestamp: new Date().toISOString(),
    environment: EnvironmentConfig.exportConfiguration(),
    features: FeatureFlags.generateReport(),
    performance: { disabled: true, message: "Performance monitoring disabled" },
    validation: ValidationService.getValidationResults(),
    logs: recentLogs.value.slice(-50), // Last 50 logs
  };

  const blob = new Blob([JSON.stringify(debugInfo, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `debug-info-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const runValidation = () => {
  debugData.value.validation = ValidationService.runFullValidation();
};

const clearLogs = () => {
  recentLogs.value = [];
};

const testSentryError = () => {
  sentryTestResult.value = null;

  try {
    // Comprehensive environment check
    const isLocal = EnvironmentConfig.isLocal;
    const isDevelopment = EnvironmentConfig.isDevelopment;
    const isProduction = EnvironmentConfig.isProduction;
    const hasErrorReporting = EnvironmentConfig.monitoring.errorReporting;
    const hasSentryDsn = !!EnvironmentConfig.monitoring.sentryDsn;
    const actualDsn = EnvironmentConfig.monitoring.sentryDsn;
    const environment = EnvironmentConfig.environment;

    console.log("🧪 Sentry Test Debug Info:", {
      isLocal,
      isDevelopment,
      isProduction,
      hasErrorReporting,
      hasSentryDsn,
      dsnPreview: actualDsn ? actualDsn.substring(0, 30) + "..." : "NOT SET",
      environment,
      sentryImported: !!Sentry,
      sentryCaptureException: typeof Sentry.captureException === "function",
      processEnv: {
        VUE_APP_ENVIRONMENT: process.env.VUE_APP_ENVIRONMENT,
        VUE_APP_ENABLE_ERROR_REPORTING:
          process.env.VUE_APP_ENABLE_ERROR_REPORTING,
        NODE_ENV: process.env.NODE_ENV,
      },
    });

    if (isLocal) {
      sentryTestResult.value = {
        success: false,
        message: `⚠️ Blocked: Environment is LOCAL (${environment}). Sentry blocks local events. Use staging/production.`,
      };
      return;
    }

    if (!hasErrorReporting) {
      sentryTestResult.value = {
        success: false,
        message: `⚠️ Error reporting disabled. VUE_APP_ENABLE_ERROR_REPORTING=${process.env.VUE_APP_ENABLE_ERROR_REPORTING}`,
      };
      return;
    }

    if (!hasSentryDsn) {
      sentryTestResult.value = {
        success: false,
        message: "⚠️ Sentry DSN not configured.",
      };
      return;
    }

    if (!Sentry || typeof Sentry.captureException !== "function") {
      sentryTestResult.value = {
        success: false,
        message:
          "⚠️ Sentry SDK not loaded. Check browser console for initialization errors.",
      };
      return;
    }

    // Trigger test error and send directly to Sentry
    const testError = new Error(
      "Test Sentry Error - Manual test from Debug Panel"
    );
    testError.name = "DebugPanelTestError";

    console.log("🚀 Sending test error to Sentry...");

    const eventId = Sentry.captureException(testError, {
      tags: {
        test: true,
        source: "debug-panel",
        environment: environment,
      },
      extra: {
        testType: "Manual Test",
        triggeredBy: "Debug Panel",
        timestamp: new Date().toISOString(),
        environmentDetails: {
          isLocal,
          isDevelopment,
          isProduction,
          actualEnvironment: environment,
        },
      },
      level: "error",
    });

    console.log("✅ Sentry event ID:", eventId);

    sentryTestResult.value = {
      success: true,
      message: `✅ Test error sent! Event ID: ${eventId}. Check Sentry dashboard (wait 5-10 sec). Environment: ${environment}`,
    };
  } catch (error) {
    console.error("❌ Sentry test failed:", error);
    sentryTestResult.value = {
      success: false,
      message: `❌ Test failed: ${error.message}`,
    };
  }
};

const collectDebugData = () => {
  const buildInfo = EnvironmentConfig.buildInfo;
  const allFlags = FeatureFlags.getAllFlags();

  debugData.value = {
    environment: {
      current: EnvironmentConfig.environment,
      debugMode: EnvironmentConfig.debugMode,
      version: buildInfo.version,
      buildTime: buildInfo.buildTime,
      gitBranch: buildInfo.gitBranch,
      gitCommit: buildInfo.gitCommit,
      configStatus: {
        firebase: !!EnvironmentConfig.firebase.projectId,
        stripe: !!EnvironmentConfig.stripe.publishableKey,
        api: !!EnvironmentConfig.api.baseUrl,
        monitoring: !!EnvironmentConfig.monitoring.sentryDsn,
      },
    },
    monitoring: {
      errorReporting: EnvironmentConfig.monitoring.errorReporting,
      hasSentryDsn: !!EnvironmentConfig.monitoring.sentryDsn,
      analytics: EnvironmentConfig.monitoring.analytics,
      hotjar: EnvironmentConfig.monitoring.hotjar,
    },
    features: {
      totalCount: Object.keys(allFlags).length,
      enabledCount: Object.values(allFlags).filter(Boolean).length,
      categories: {
        core: FeatureFlags.core,
        payments: FeatureFlags.payments,
        admin: FeatureFlags.admin,
        development: FeatureFlags.development,
        integrations: FeatureFlags.integrations,
        performance: FeatureFlags.performance,
        experimental: FeatureFlags.experimental,
      },
    },
    performance: {
      memory: getMemoryInfo(),
      coreWebVitals: null,
      recentEvents: getRecentPerformanceEvents(),
    },
    validation: ValidationService.getValidationResults(),
  };
};

const getMemoryInfo = () => {
  if (typeof performance !== "undefined" && "memory" in performance) {
    const memory = performance.memory;
    return {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
      percentage: Math.round(
        (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      ),
    };
  }
  return null;
};

const getRecentPerformanceEvents = () => {
  // This would integrate with your performance monitoring
  return [
    {
      id: 1,
      name: "Component Mount",
      duration: 45,
      timestamp: Date.now() - 1000,
    },
    { id: 2, name: "API Call", duration: 230, timestamp: Date.now() - 2000 },
    { id: 3, name: "Route Change", duration: 12, timestamp: Date.now() - 3000 },
  ];
};

// Utility methods
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString();
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString();
};

const getStatusColor = (status) => {
  if (typeof status === "boolean") {
    return status ? "text-green-600" : "text-red-600";
  }
  return "text-gray-600";
};

const getVitalColor = (rating) => {
  switch (rating) {
    case "good":
      return "text-green-600";
    case "needs-improvement":
      return "text-orange-600";
    case "poor":
      return "text-red-600";
    default:
      return "text-gray-600";
  }
};

const getValidationColor = (result) => {
  switch (result) {
    case "pass":
      return "text-green-600";
    case "fail":
      return "text-red-600";
    case "warn":
      return "text-orange-600";
    default:
      return "text-gray-600";
  }
};

const getLogColor = (level) => {
  const colors = {
    error: "bg-red-50 border-red-400 text-red-800",
    warn: "bg-orange-50 border-orange-400 text-orange-800",
    info: "bg-blue-50 border-blue-400 text-blue-800",
    debug: "bg-gray-50 border-gray-400 text-gray-800",
  };
  return colors[level] || colors.debug;
};

// Custom logger to capture logs for display
const originalLogMethod = Logger.log;
Logger.log = function (level, message, data, context) {
  // Call original log method
  const result = originalLogMethod.call(this, level, message, data, context);

  // Add to our recent logs
  recentLogs.value.unshift({
    id: Date.now() + Math.random(),
    level,
    message,
    data,
    context,
    timestamp: Date.now(),
  });

  // Keep only the most recent logs
  if (recentLogs.value.length > props.maxLogs) {
    recentLogs.value = recentLogs.value.slice(0, props.maxLogs);
  }

  return result;
};

// Lifecycle
onMounted(() => {
  if (isVisible.value) {
    collectDebugData();
  }
});

onUnmounted(() => {
  // Restore original logger if needed
  if (originalLogMethod) {
    Logger.log = originalLogMethod;
  }
});
</script>

<style scoped>
.debug-panel {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
}

.debug-toggle {
  backdrop-filter: blur(10px);
}

.debug-content {
  position: fixed;
  bottom: 80px;
  left: 4px;
  width: 600px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 120px);
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 40;
  overflow: hidden;
}

.debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.debug-tabs {
  display: flex;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
}

.debug-tab-content {
  height: 400px;
  overflow-y: auto;
}

.tab-panel {
  padding: 1rem;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .debug-content {
    width: calc(100vw - 8px);
    right: 4px;
    left: 4px;
    bottom: 70px;
  }

  .debug-tabs {
    font-size: 0.75rem;
  }

  .debug-tab-content {
    height: 300px;
  }
}
</style>
